import React, { useEffect, useState } from 'react';
import axios from 'axios';
import SEO from "../../common/SEO";
import Layout from "../../common/Layout";
import { useParams, Redirect } from 'react-router-dom';
import ReactPlayer from 'react-player';
import { FaListUl } from "react-icons/fa";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const CoursePlay = () => {
    const { slug } = useParams();
    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
    const [completed, setCompleted] = useState([]);
    const [redirect, setRedirect] = useState(false);
    const [showPlaylist, setShowPlaylist] = useState(false);

    const token = localStorage.getItem("token");
    const purchased = JSON.parse(localStorage.getItem("purchased_courses") || "[]");
    const hasAccess = purchased.includes(slug);

    useEffect(() => {
        if (!hasAccess) {
            setRedirect(true);
            return;
        }

        const fetchCourseData = async () => {
            setLoading(true);
            const headers = token ? { Authorization: `Bearer ${token}` } : {};

            try {
                const [courseRes, progressRes] = await Promise.all([
                    axios.get(`${API_BASE_URL}/courses/${slug}`, { headers }),
                    token
                        ? axios.get(`${API_BASE_URL}/courses/${slug}/progress`, { headers })
                        : Promise.resolve({ data: { completed_videos: [] } }),
                ]);

                if (!courseRes.data?.data) {
                    setRedirect(true);
                } else {
                    setCourse(courseRes.data.data);
                    setCompleted(progressRes.data.completed_videos || []);
                }
            } catch (error) {
                console.error("Fetch error:", error);
                setRedirect(true);
            } finally {
                setLoading(false);
            }
        };

        fetchCourseData();
    }, [slug, token, hasAccess]);

    const handleVideoEnd = () => {
        if (!completed.includes(currentVideoIndex)) {
            const updatedCompleted = [...completed, currentVideoIndex];
            setCompleted(updatedCompleted);

            if (token) {
                axios.post(`${API_BASE_URL}/courses/${slug}/progress`, {
                    video_index: currentVideoIndex
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                }).catch((err) => console.error("Progress save error", err));
            }
        }
    };

    if (!hasAccess || redirect) return <Redirect to="/courses" />;
    if (loading) return <p className="text-center mt-5">Loading course details...</p>;
    if (!course) return <p className="text-center mt-5 text-danger">Course not found.</p>;

    const playlist = course.playlist || [];
    const progress = playlist.length
        ? Math.round((completed.length / playlist.length) * 100)
        : 0;

    return (
        <>
            <SEO title={`${course.title} || PWU`} />
            <Layout>
                {/* Mobile Playlist Toggle Icon */}
                <div
                    className="d-lg-none position-fixed"
                    style={{
                        top: "80px", // below header
                        left: "10px",
                        zIndex: 1050,
                        cursor: "pointer",
                        // margin: "10px",
                    }}
                    onClick={() => setShowPlaylist(true)}
                >
                    <FaListUl size={26} title="Show Playlist" />
                </div>

                {/* Mobile Overlay Playlist */}
                <div
                    className="d-lg-none position-fixed"
                    style={{
                        top: "80px",          // below header
                        bottom: "0",       // above footer
                        left: 0,
                        width: "70%",         // 70% width
                        background: "rgba(0,0,0)", // gray 80% transparent
                        zIndex: 1040,
                        overflowY: "auto",
                        transition: "transform 0.3s ease-in-out, opacity 0.3s ease-in-out",
                        transform: showPlaylist ? "translateX(0)" : "translateX(-100%)",
                    }}
                >
                    <div className="p-3">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="mb-0 pt-5">{course.title} - Playlist</h5>
                            <button
                                className="btn btn-sm btn-outline-secondary text-white"
                                onClick={() => setShowPlaylist(false)}
                            >
                                ✕
                            </button>
                        </div>
                        <ul className="list-group">
                            {playlist.map((video, index) => (
                                <li
                                    key={index}
                                    className={`list-group-item d-flex justify-content-between align-items-center ${
                                        index === currentVideoIndex ? "active" : ""
                                    }`}
                                    style={{
                                        cursor: "pointer",
                                        background: "transparent",
                                        border: "none",
                                        color: index === currentVideoIndex ? "#0d6efd" : "#fff",
                                        fontWeight: index === currentVideoIndex ? "600" : "400",
                                    }}
                                    onClick={() => {
                                        setCurrentVideoIndex(index);
                                        setShowPlaylist(false);
                                    }}
                                >
                                    {video.title}
                                    {completed.includes(index)
                                        ? <span>✅</span>
                                        : index === currentVideoIndex
                                            ? <span>▶️</span>
                                            : null
                                    }
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Main Container */}
                <div className="container mt-5 d-flex flex-wrap gap-4">
                    {/* Sidebar Playlist - Visible only on Desktop */}
                    <aside
                        className="d-none d-lg-block"
                        style={{
                            flex: "0 0 300px",
                            maxHeight: "80vh",
                            overflowY: "auto",
                            borderRight: "1px solid #ddd",
                            paddingRight: "15px",
                        }}
                    >
                        <h4 className="mb-4">{course.title} - Playlist</h4>
                        <ul className="list-group">
                            {playlist.map((video, index) => (
                                <li
                                    key={index}
                                    className={`list-group-item d-flex justify-content-between align-items-center ${
                                        index === currentVideoIndex ? "active" : ""
                                    }`}
                                    style={{ cursor: "pointer" }}
                                    onClick={() => setCurrentVideoIndex(index)}
                                >
                                    {video.title}
                                    {completed.includes(index)
                                        ? <span>✅</span>
                                        : index === currentVideoIndex
                                            ? <span>▶️</span>
                                            : null
                                    }
                                </li>
                            ))}
                        </ul>
                    </aside>

                    {/* Video Section */}
                    <main style={{ flex: "1", minWidth: "300px" }}>
                        <div className="mb-3">
                            <div className="progress" style={{ height: "10px", padding: "10px" }}>
                                <div
                                    className="progress-bar bg-success"
                                    role="progressbar"
                                    style={{ width: `${progress}%` }}
                                    aria-valuenow={progress}
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                ></div>
                            </div>
                            <small className="text-muted">{progress}% Complete</small>
                        </div>

                        {/* Responsive YouTube Format */}
                        <div style={{
                            position: "relative",
                            paddingBottom: "56.25%", // 16:9 aspect ratio
                            height: 0,
                            overflow: "hidden",
                            borderRadius: "10px",
                        }}>
                            {playlist.length > 0 && playlist[currentVideoIndex] ? (
                                <ReactPlayer
                                    url={playlist[currentVideoIndex].url}
                                    controls
                                    width="100%"
                                    height="100%"
                                    style={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                    }}
                                    onEnded={handleVideoEnd}
                                    config={{
                                        youtube: {
                                            playerVars: { rel: 0, modestbranding: 1 },
                                            embedOptions: {
                                                referrerPolicy: "strict-origin-when-cross-origin",
                                            },
                                        },
                                    }}
                                />
                            ) : (
                                <p>No videos available in this course.</p>
                            )}
                        </div>
                    </main>
                </div>
            </Layout>
        </>
    );
};

export default CoursePlay;
